/**
 * AvailabilityNoteModal Component
 *
 * Lets a crew member add or edit the note attached to their availability for
 * an event — independently of whether they've picked a status yet. Saves via
 * the availability upsert WITHOUT a status field, so an existing status is
 * preserved and the save works even inside the 24h status lockout.
 */

import { useState } from 'react';
import { setAvailability } from '../../api/availability';
import { getErrorMessage } from '../../api/client';
import { Modal } from '../ui';

interface AvailabilityNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  eventId: string;
  eventName: string;
  currentNote?: string | null;
}

export default function AvailabilityNoteModal({
  isOpen,
  onClose,
  onSuccess,
  eventId,
  eventName,
  currentNote,
}: AvailabilityNoteModalProps) {
  const [note, setNote] = useState(currentNote || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!isSubmitting) {
      setNote(currentNote || '');
      setError(null);
      onClose();
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await setAvailability(eventId, { notes: note.trim() || null });
      onSuccess();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" labelledBy="availability-note-title">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 id="availability-note-title" className="text-lg font-semibold text-gray-900">
          {currentNote ? 'Edit Note' : 'Add Note'}
        </h3>
        <p className="text-sm text-gray-500 mt-1">{eventName}</p>
      </div>

      {/* Content */}
      <div className="px-6 py-4 space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="availability-note" className="block text-sm font-medium text-gray-700 mb-1">
            Note for the scheduler
          </label>
          <textarea
            id="availability-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={500}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
            placeholder="e.g., Available after class, there by 5pm..."
          />
          <p className="text-xs text-gray-400 mt-1">{note.length}/500 characters</p>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-lg">
        <button
          type="button"
          onClick={handleClose}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Saving...' : 'Save Note'}
        </button>
      </div>
    </Modal>
  );
}
